import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const CURRENCY_META = {
  GBP: { symbol: "£", label: "GBP", locale: "en-GB" },
  INR: { symbol: "₹", label: "INR", locale: "en-IN" },
  USD: { symbol: "$", label: "USD", locale: "en-US" },
  EUR: { symbol: "€", label: "EUR", locale: "de-DE" },
};

const FALLBACK_RATES = { GBP: 1, INR: 105, USD: 1.27, EUR: 1.17 };

const SUPPORTED = ["GBP", "INR", "USD", "EUR"];
const EUR_LANGS = ["de", "fr", "es", "it", "nl", "pt", "el", "fi", "ga", "sk", "sl", "et", "lv", "lt", "mt"];

// Best-effort currency guess from the visitor's timezone / locale (no external call).
function detectCurrency() {
  try {
    const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || "");
    if (/Kolkata|Calcutta/i.test(tz)) return "INR";
    if (tz.startsWith("America/")) return "USD";
    if (tz === "Europe/London") return "GBP";
    if (tz.startsWith("Europe/")) return "EUR";
  } catch (e) { /* ignore */ }
  const loc = (navigator.language || "").toLowerCase();
  if (loc.endsWith("-in")) return "INR";
  if (loc.endsWith("-us")) return "USD";
  if (loc.endsWith("-gb") || loc === "en") return "GBP";
  if (EUR_LANGS.some((l) => loc.startsWith(l))) return "EUR";
  return "GBP";
}

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    const saved = localStorage.getItem("zebaa_currency");
    if (saved && SUPPORTED.includes(saved)) return saved;
    const detected = detectCurrency();
    return SUPPORTED.includes(detected) ? detected : "GBP";
  });
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [currencies, setCurrencies] = useState(["GBP", "INR", "USD", "EUR"]);

  useEffect(() => {
    api.get("/fx-rates")
      .then(({ data }) => {
        if (data.rates) setRates(data.rates);
        if (data.currencies) setCurrencies(data.currencies);
      })
      .catch(() => {});
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    localStorage.setItem("zebaa_currency", c);
  }, []);

  // Convert a GBP amount into the selected currency and format it.
  const format = useCallback((gbpAmount) => {
    const meta = CURRENCY_META[currency] || CURRENCY_META.GBP;
    const rate = rates[currency] || 1;
    const value = (Number(gbpAmount) || 0) * rate;
    const noDecimals = currency === "INR";
    return new Intl.NumberFormat(meta.locale, {
      style: "currency",
      currency,
      minimumFractionDigits: noDecimals ? 0 : 2,
      maximumFractionDigits: noDecimals ? 0 : 2,
    }).format(value);
  }, [currency, rates]);

  const value = {
    currency, setCurrency, currencies, rates,
    meta: CURRENCY_META,
    isConverted: currency !== "GBP",
    format,
  };
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Safe fallback so components never crash if used outside the provider.
    return {
      currency: "GBP", setCurrency: () => {}, currencies: ["GBP"], rates: FALLBACK_RATES,
      meta: CURRENCY_META, isConverted: false,
      format: (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(v) || 0),
    };
  }
  return ctx;
};

export const CurrencySelector = ({ className = "", testid = "currency-selector" }) => {
  const { currency, setCurrency, currencies, meta } = useCurrency();
  return (
    <select
      value={currency}
      onChange={(e) => setCurrency(e.target.value)}
      data-testid={testid}
      aria-label="Select currency"
      className={`bg-transparent text-[11px] uppercase tracking-[0.08em] text-charcoal border border-gray-200 rounded px-1.5 py-1 leading-none focus:outline-none focus:border-pink cursor-pointer hover:border-pink transition-colors ${className}`}
    >
      {currencies.map((c) => (
        <option key={c} value={c}>{meta[c]?.symbol} {c}</option>
      ))}
    </select>
  );
};
